import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import {
  ClockCircleOutlined,
  GiftOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  TeamOutlined,
  CrownOutlined,
  StopOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { bubbleRewardAPI, configAPI } from '../services/api';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const MAX_REWARD_COUNT = 2147483647;

function BubbleRewardManagement() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [activeWorkspace, setActiveWorkspace] = useState('config');
  const [itemOptions, setItemOptions] = useState([]);
  const [config, setConfig] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [vipRecords, setVipRecords] = useState([]);
  const [grantLogs, setGrantLogs] = useState([]);
  const [onlinePagination, setOnlinePagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [vipPagination, setVipPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [logPagination, setLogPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [vipKeyword, setVipKeyword] = useState('');
  const [vipStatus, setVipStatus] = useState(undefined);
  const [form] = Form.useForm();
  const baseRewardItems = Form.useWatch('base_items', form) || [];
  const vipRewardItems = Form.useWatch('vip_items', form) || [];
  const [manualVipVisible, setManualVipVisible] = useState(false);
  const [manualVipSubmitting, setManualVipSubmitting] = useState(false);
  const [manualVipForm] = Form.useForm();
  const [itemCatalog, setItemCatalog] = useState([]);
  const [rewardPickerVisible, setRewardPickerVisible] = useState(false);
  const [rewardPickerType, setRewardPickerType] = useState('base_items');
  const [pickerSelectedItems, setPickerSelectedItems] = useState([]);
  const [pickerSearchText, setPickerSearchText] = useState('');
  const [levelModalVisible, setLevelModalVisible] = useState(false);
  const [currentItemForLevel, setCurrentItemForLevel] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const itemCatalogMap = useMemo(
    () => new Map(itemCatalog.map((item) => [String(item.id), item])),
    [itemCatalog]
  );
  const filteredItemCatalog = useMemo(() => {
    const searchText = pickerSearchText.toLowerCase().trim();
    if (!searchText) return itemCatalog;
    return itemCatalog.filter((item) =>
      String(item.id).includes(searchText) || (item.name || '').toLowerCase().includes(searchText)
    );
  }, [itemCatalog, pickerSearchText]);
  const workspaceTabs = useMemo(() => ([
    { key: 'config', label: '规则配置', icon: <SettingOutlined /> },
    { key: 'online', label: '在线列表', icon: <TeamOutlined /> },
    { key: 'vip', label: 'VIP记录', icon: <CrownOutlined /> },
    { key: 'logs', label: '发放日志', icon: <ClockCircleOutlined /> }
  ]), []);

  const loadItemOptions = async () => {
    try {
      const response = await configAPI.getItems();
      const items = response.data?.data || [];
      setItemCatalog(items.map((item) => ({
        id: String(item.item_id || item.id),
        name: item.name || `道具${item.item_id || item.id}`,
        levels: item.levels || [],
        defaultLevel: item.defaultLevel || 0,
        quality: item.quality || 0,
        description: item.description || '',
        type: item.type || 'item'
      })));
      setItemOptions(
        items.map((item) => ({
          value: String(item.item_id || item.id),
          label: `${item.name || `道具${item.item_id || item.id}`} (#${item.item_id || item.id})`
        }))
      );
    } catch (error) {
      setItemCatalog([]);
      setItemOptions([]);
    }
  };

  const hydratePickerItems = (items = []) => items.map((item) => {
    const matchedItem = itemCatalog.find((catalogItem) => String(catalogItem.id) === String(item.id));
    const level = Number(item.level || 0);
    return {
      id: String(item.id),
      name: matchedItem?.name || `道具${item.id}`,
      levels: matchedItem?.levels || [],
      quality: matchedItem?.quality || 0,
      description: matchedItem?.description || '',
      type: matchedItem?.type || 'item',
      count: Number(item.count || 1),
      level,
      uniqueId: `${item.id}_${level}`
    };
  });

  const handleOpenRewardPicker = (listName) => {
    setRewardPickerType(listName);
    setPickerSearchText('');
    setPickerSelectedItems(hydratePickerItems(form.getFieldValue(listName) || []));
    setRewardPickerVisible(true);
  };

  const handleSelectRewardItem = (item) => {
    if (item.levels && item.levels.length > 0) {
      setCurrentItemForLevel(item);
      setSelectedLevel(item.levels[0].level);
      setLevelModalVisible(true);
      return;
    }

    const level = item.defaultLevel || 0;
    const uniqueId = `${item.id}_${level}`;
    if (pickerSelectedItems.some((selectedItem) => selectedItem.uniqueId === uniqueId)) {
      message.info('该道具已添加到奖励中');
      return;
    }

    setPickerSelectedItems([...pickerSelectedItems, { ...item, level, count: 1, uniqueId }]);
  };

  const handleConfirmPickerLevel = () => {
    if (!currentItemForLevel || selectedLevel === null || selectedLevel === undefined) return;
    const uniqueId = `${currentItemForLevel.id}_${selectedLevel}`;
    if (pickerSelectedItems.some((selectedItem) => selectedItem.uniqueId === uniqueId)) {
      message.info('该道具已添加到奖励中');
      return;
    }
    setPickerSelectedItems([...pickerSelectedItems, { ...currentItemForLevel, level: selectedLevel, count: 1, uniqueId }]);
    setLevelModalVisible(false);
    setCurrentItemForLevel(null);
    setSelectedLevel(null);
  };

  const handleConfirmRewardPicker = () => {
    form.setFieldValue(rewardPickerType, pickerSelectedItems.map((item) => ({
      id: item.id,
      count: item.count,
      level: item.level || 0
    })));
    setRewardPickerVisible(false);
    message.success(`已同步 ${pickerSelectedItems.length} 个奖励到${rewardPickerType === 'base_items' ? '基础奖励' : 'VIP奖励'}`);
  };

  const normalizeItemsForForm = (items) =>
    Array.isArray(items)
      ? items.map((item) => ({
          id: String(item.id),
          count: item.count,
          level: item.level || 0
        }))
      : [];

  const loadConfig = async () => {
    const response = await bubbleRewardAPI.getConfig();
    const data = response.data.data;
    setConfig(data);
    form.setFieldsValue({
      name: data.name,
      enabled: !!data.enabled,
      interval_value: data.interval_value,
      interval_unit: data.interval_unit,
      online_keepalive_seconds: data.online_keepalive_seconds,
      reward_title: data.reward_title,
      reward_content: data.reward_content,
      vip_reward_content: data.vip_reward_content,
      base_items: normalizeItemsForForm(data.base_items),
      vip_items: normalizeItemsForForm(data.vip_items)
    });
  };

  const loadOnlineUsers = async (page = onlinePagination.current, pageSize = onlinePagination.pageSize) => {
    const response = await bubbleRewardAPI.getOnlineUsers({ page, limit: pageSize });
    const data = response.data.data;
    setOnlineUsers(data.list || []);
    setOnlinePagination({
      current: data.pagination.page,
      pageSize: data.pagination.limit,
      total: data.pagination.total
    });
  };

  const loadVipRecords = async (page = vipPagination.current, pageSize = vipPagination.pageSize, overrides = {}) => {
    const response = await bubbleRewardAPI.getVipRecords({
      page,
      limit: pageSize,
      keyword: overrides.keyword !== undefined ? overrides.keyword || undefined : vipKeyword || undefined,
      status: overrides.status !== undefined ? overrides.status || undefined : vipStatus || undefined
    });
    const data = response.data.data;
    setVipRecords(data.list || []);
    setVipPagination({
      current: data.pagination.page,
      pageSize: data.pagination.limit,
      total: data.pagination.total
    });
  };

  const loadLogs = async (page = logPagination.current, pageSize = logPagination.pageSize) => {
    const response = await bubbleRewardAPI.getLogs({ page, limit: pageSize });
    const data = response.data.data;
    setGrantLogs(data.list || []);
    setLogPagination({
      current: data.pagination.page,
      pageSize: data.pagination.limit,
      total: data.pagination.total
    });
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadItemOptions(), loadConfig(), loadOnlineUsers(1, onlinePagination.pageSize), loadVipRecords(1, vipPagination.pageSize), loadLogs(1, logPagination.pageSize)]);
    } catch (error) {
      message.error(error.response?.data?.message || '加载泡点配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statInfo = useMemo(() => {
    const vipOnlineCount = onlineUsers.filter((item) => item.bubble_vip_active).length;
    return {
      onlineCount: onlinePagination.total || 0,
      vipOnlineCount,
      vipTotal: vipPagination.total || 0,
      logTotal: logPagination.total || 0
    };
  }, [onlineUsers, onlinePagination.total, vipPagination.total, logPagination.total]);

  const intervalLabel = useMemo(() => {
    if (!config) return '--';
    const unitMap = {
      second: '秒',
      minute: '分钟',
      hour: '小时'
    };
    return `${config.interval_value || '--'}${unitMap[config.interval_unit] || ''}`;
  }, [config]);

  const currentVipActiveCount = useMemo(
    () => vipRecords.filter((record) => record.active).length,
    [vipRecords]
  );

  const currentVipExpiredCount = useMemo(
    () => vipRecords.filter((record) => !record.active).length,
    [vipRecords]
  );

  const currentLogVipCount = useMemo(
    () => grantLogs.filter((record) => record.is_vip).length,
    [grantLogs]
  );

  const getItemDisplayName = (itemId) => itemCatalogMap.get(String(itemId))?.name || `道具${itemId}`;

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await bubbleRewardAPI.saveConfig(values);
      message.success('泡点配置保存成功');
      await loadConfig();
    } catch (error) {
      message.error(error.response?.data?.message || '保存泡点配置失败');
    } finally {
      setSaving(false);
    }
  };

  const handleEnabledChange = async (checked) => {
    form.setFieldValue('enabled', checked);
    try {
      const values = await form.validateFields();
      await handleSave(values);
    } catch (error) {
      if (error?.errorFields) {
        message.error('请先完善泡点配置后再启用');
      }
      form.setFieldValue('enabled', !checked);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const response = await bubbleRewardAPI.runNow();
      const result = response.data.data;
      message.success(`执行完成，在线 ${result.totalCount} 人，发放 ${result.sentCount} 人`);
      await Promise.all([loadConfig(), loadOnlineUsers(1, onlinePagination.pageSize), loadVipRecords(vipPagination.current, vipPagination.pageSize), loadLogs(1, logPagination.pageSize)]);
    } catch (error) {
      message.error(error.response?.data?.message || '立即执行失败');
    } finally {
      setRunning(false);
    }
  };

  const handleDeactivateVip = (record) => {
    Modal.confirm({
      title: '停用泡点VIP',
      content: `确认停用玩家 ${record.name || record.user_id} 的泡点VIP吗？`,
      okText: '停用',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await bubbleRewardAPI.deactivateVip(record.user_id);
        message.success('泡点VIP已停用');
        await Promise.all([loadVipRecords(vipPagination.current, vipPagination.pageSize), loadOnlineUsers(onlinePagination.current, onlinePagination.pageSize)]);
      }
    });
  };

  const handleManualVipSubmit = async (values) => {
    setManualVipSubmitting(true);
    try {
      await bubbleRewardAPI.grantManualVip(values);
      message.success('泡点VIP手动发放成功');
      setManualVipVisible(false);
      manualVipForm.resetFields();
      await Promise.all([
        loadVipRecords(1, vipPagination.pageSize),
        loadOnlineUsers(onlinePagination.current, onlinePagination.pageSize)
      ]);
    } catch (error) {
      message.error(error.response?.data?.message || '泡点VIP手动发放失败');
    } finally {
      setManualVipSubmitting(false);
    }
  };

  const itemColumns = () => [
    {
      title: '道具ID',
      dataIndex: 'id',
      width: 260,
      render: (_, field) => (
        <Form.Item
          name={[field.name, 'id']}
          rules={[{ required: true, message: '请选择道具' }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            showSearch
            placeholder="选择道具"
            optionFilterProp="label"
            options={itemOptions}
          />
        </Form.Item>
      )
    },
    {
      title: '数量',
      dataIndex: 'count',
      width: 120,
      render: (_, field) => (
        <Form.Item
          name={[field.name, 'count']}
          rules={[{ required: true, message: '数量' }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
      )
    },
    {
      title: '等级',
      dataIndex: 'level',
      width: 120,
      render: (_, field) => (
        <Form.Item
          name={[field.name, 'level']}
          style={{ marginBottom: 0 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      )
    }
  ];

  const onlineColumns = [
    { title: '玩家ID', dataIndex: 'user_id', width: 110 },
    { title: '名称', dataIndex: 'name', width: 160 },
    { title: '等级', dataIndex: 'level', width: 80 },
    { title: '游戏VIP', dataIndex: 'vip_lv', width: 90 },
    {
      title: '泡点VIP',
      dataIndex: 'bubble_vip_active',
      width: 150,
      render: (value, record) =>
        value ? (
          <Space direction="vertical" size={0}>
            <Tag color="gold">已开通</Tag>
            <Text type="secondary">{record.bubble_vip_end_time}</Text>
          </Space>
        ) : (
          <Tag>无</Tag>
        )
    },
    { title: '最近同步', dataIndex: 'last_sync_time', width: 120 }
  ];

  const vipColumns = [
    { title: '玩家ID', dataIndex: 'user_id', width: 110 },
    { title: '角色名', dataIndex: 'name', width: 150 },
    { title: '商品', dataIndex: 'product_name', width: 160 },
    { title: '订单号', dataIndex: 'last_order_no', width: 180 },
    { title: '首次开通', dataIndex: 'first_open_time', width: 160 },
    { title: '最近购买', dataIndex: 'last_purchase_time', width: 160 },
    { title: '开始时间', dataIndex: 'start_time', width: 160 },
    { title: '结束时间', dataIndex: 'end_time', width: 160 },
    { title: '累计购买', dataIndex: 'purchase_count', width: 100 },
    { title: '累计时长', dataIndex: 'total_duration_label', width: 120 },
    {
      title: '状态',
      dataIndex: 'active',
      width: 90,
      render: (active) => <Tag color={active ? 'green' : 'default'}>{active ? '生效中' : '已失效'}</Tag>
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) =>
        record.active ? (
          <Button danger size="small" icon={<StopOutlined />} onClick={() => handleDeactivateVip(record)}>
            停用
          </Button>
        ) : null
    }
  ];

  const logColumns = [
    { title: '批次', dataIndex: 'cycle_key', width: 180 },
    { title: '玩家ID', dataIndex: 'user_id', width: 100 },
    { title: '角色名', dataIndex: 'user_name', width: 150 },
    {
      title: '类型',
      dataIndex: 'is_vip',
      width: 90,
      render: (value) => <Tag color={value ? 'gold' : 'blue'}>{value ? 'VIP' : '普通'}</Tag>
    },
    {
      title: '奖励内容',
      dataIndex: 'final_items',
      render: (items) =>
        Array.isArray(items) && items.length > 0
          ? items.map((item) => (
              <Tag key={`${item.id}-${item.level || 0}`} style={{ marginBottom: 4 }}>
                #{item.id} x{item.count}{item.level ? ` Lv.${item.level}` : ''}
              </Tag>
            ))
          : <Text type="secondary">无</Text>
    },
    { title: '发放时间', dataIndex: 'created_at', width: 170 }
  ];

  const renderRewardSummary = (title, items, color) => (
    <div className="bubble-reward-summary-item">
      <div className="bubble-reward-summary-head">
        <div className="bubble-reward-summary-title">{title}</div>
        <Tag color={color}>{items.length} 项</Tag>
      </div>
      {items.length > 0 ? (
        <div className="bubble-reward-chip-row">
          {items.slice(0, 6).map((item) => (
            <Tag key={`${title}-${item.id}-${item.level || 0}`}>
              {getItemDisplayName(item.id)}
              {item.level ? ` Lv.${item.level}` : ''}
              {item.count ? ` x${item.count}` : ''}
            </Tag>
          ))}
          {items.length > 6 && <Tag>+{items.length - 6}</Tag>}
        </div>
      ) : (
        <Text type="secondary">未配置</Text>
      )}
    </div>
  );

  return (
    <div className="page-shell mall-workspace-shell bubble-workspace-shell">
      <div className="mall-command-deck">
        <div className="mall-command-deck-head">
          <div className="mall-command-copy">
            <div className="mall-command-title">泡点管理</div>
          </div>
          <div className="mall-command-metrics">
            <div className="mall-command-metric">
              <span>在线人数</span>
              <strong>{statInfo.onlineCount}</strong>
            </div>
            <div className="mall-command-metric">
              <span>在线 VIP</span>
              <strong>{statInfo.vipOnlineCount}</strong>
            </div>
            <div className="mall-command-metric">
              <span>VIP 记录</span>
              <strong>{statInfo.vipTotal}</strong>
            </div>
            <div className="mall-command-metric">
              <span>发放日志</span>
              <strong>{statInfo.logTotal}</strong>
            </div>
          </div>
        </div>

        <div className="mall-workspace-tabs bubble-workspace-tabs">
          {workspaceTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`mall-workspace-tab${activeWorkspace === tab.key ? ' mall-workspace-tab-active' : ''}`}
              onClick={() => setActiveWorkspace(tab.key)}
            >
              <span className="mall-workspace-tab-icon">{tab.icon}</span>
              <span className="mall-workspace-tab-body">
                <strong>{tab.label}</strong>
              </span>
            </button>
          ))}
        </div>
      </div>

      {activeWorkspace === 'config' && (
        <>
          <div className="mall-section-banner">
            <div className="mall-section-banner-copy">
              <div className="mall-section-banner-title">规则配置</div>
            </div>
            <div className="mall-section-banner-tags">
              <Tag color={config?.enabled ? 'green' : 'default'}>{config?.enabled ? '启用中' : '未启用'}</Tag>
              <Tag color="blue">间隔 {intervalLabel}</Tag>
              <Tag color="purple">基础奖励 {baseRewardItems.length}</Tag>
              <Tag color="gold">VIP奖励 {vipRewardItems.length}</Tag>
            </div>
          </div>

          <div className="mall-command-panel">
            <div className="mall-command-panel-main bubble-command-statuses">
              <div className="bubble-inline-chip">
                <span>最近发放</span>
                <strong>{config?.last_grant_time || '未执行'}</strong>
              </div>
              <div className="bubble-inline-chip">
                <span>下一次预计</span>
                <strong>{config?.next_grant_time || '保存后计算'}</strong>
              </div>
              <div className="bubble-inline-chip">
                <span>在线判定</span>
                <strong>{config?.online_keepalive_seconds || '--'} 秒</strong>
              </div>
            </div>
            <div className="mall-command-panel-actions">
              <Button icon={<ReloadOutlined />} onClick={loadAll} className="rounded-button">刷新</Button>
              <Button type="primary" icon={<PlayCircleOutlined />} loading={running} onClick={handleRunNow} className="rounded-button">立即执行</Button>
              <Button type="primary" loading={saving} onClick={() => form.submit()} className="rounded-button">保存配置</Button>
            </div>
          </div>

          <div className="bubble-config-layout">
            <Card className="section-card bubble-config-main-card" loading={loading} bodyStyle={{ padding: 24 }}>
              <Form form={form} layout="vertical" onFinish={handleSave}>
                <div className="bubble-form-grid">
                  <Form.Item className="bubble-field-span-4" name="name" label="配置名称" rules={[{ required: true, message: '请输入配置名称' }]}>
                    <Input placeholder="请输入配置名称" />
                  </Form.Item>
                  <Form.Item className="bubble-field-span-2" name="enabled" label="启用状态" valuePropName="checked">
                    <Switch checkedChildren="启用" unCheckedChildren="停用" onChange={handleEnabledChange} loading={saving} />
                  </Form.Item>
                  <Form.Item className="bubble-field-span-3" name="interval_value" label="发放间隔值" rules={[{ required: true, message: '请输入间隔值' }]}>
                    <InputNumber min={1} className="field-width-full" />
                  </Form.Item>
                  <Form.Item className="bubble-field-span-3" name="interval_unit" label="发放间隔单位" rules={[{ required: true, message: '请选择单位' }]}>
                    <Select placeholder="选择单位">
                      <Option value="second">秒</Option>
                      <Option value="minute">分钟</Option>
                      <Option value="hour">小时</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item className="bubble-field-span-3" name="online_keepalive_seconds" label="在线判定秒数" rules={[{ required: true, message: '请输入秒数' }]}>
                    <InputNumber min={60} step={60} className="field-width-full" />
                  </Form.Item>
                  <Form.Item className="bubble-field-span-4" name="reward_title" label="普通奖励邮件标题" rules={[{ required: true, message: '请输入邮件标题' }]}>
                    <Input placeholder="请输入普通奖励邮件标题" />
                  </Form.Item>
                  <Form.Item className="bubble-field-span-5" name="reward_content" label="普通奖励邮件内容" rules={[{ required: true, message: '请输入邮件内容' }]}>
                    <TextArea rows={3} placeholder="请输入普通奖励邮件内容" />
                  </Form.Item>
                  <Form.Item className="bubble-field-span-12" name="vip_reward_content" label="VIP奖励邮件内容" rules={[{ required: true, message: '请输入VIP邮件内容' }]}>
                    <TextArea rows={3} placeholder="请输入VIP奖励邮件内容" />
                  </Form.Item>
                </div>

                <div className="bubble-editor-stack">
                  <div className="bubble-editor-block">
                    <div className="bubble-editor-block-head">
                      <div className="bubble-editor-block-title">基础奖励</div>
                      <div className="bubble-editor-block-actions">
                        <Button type="primary" icon={<GiftOutlined />} onClick={() => handleOpenRewardPicker('base_items')} className="rounded-button">从物品库选择</Button>
                        <Button onClick={() => form.setFieldValue('base_items', [...baseRewardItems, { count: 1, level: 0 }])} className="rounded-button">手动添加</Button>
                      </div>
                    </div>
                    <Form.List name="base_items">
                      {(fields, { remove }) => (
                        <Table
                          rowKey="key"
                          pagination={false}
                          dataSource={fields}
                          columns={[
                            ...itemColumns(),
                            {
                              title: '操作',
                              key: 'action',
                              width: 88,
                              render: (_, __, index) => (
                                <Button danger type="link" onClick={() => remove(index)}>删除</Button>
                              )
                            }
                          ]}
                          scroll={{ x: 720 }}
                          locale={{ emptyText: '暂无基础奖励' }}
                        />
                      )}
                    </Form.List>
                  </div>

                  <div className="bubble-editor-block">
                    <div className="bubble-editor-block-head">
                      <div className="bubble-editor-block-title">VIP额外奖励</div>
                      <div className="bubble-editor-block-actions">
                        <Button type="primary" icon={<GiftOutlined />} onClick={() => handleOpenRewardPicker('vip_items')} className="rounded-button">从物品库选择</Button>
                        <Button onClick={() => form.setFieldValue('vip_items', [...vipRewardItems, { count: 1, level: 0 }])} className="rounded-button">手动添加</Button>
                      </div>
                    </div>
                    <Form.List name="vip_items">
                      {(fields, { remove }) => (
                        <Table
                          rowKey="key"
                          pagination={false}
                          dataSource={fields}
                          columns={[
                            ...itemColumns(),
                            {
                              title: '操作',
                              key: 'action',
                              width: 88,
                              render: (_, __, index) => (
                                <Button danger type="link" onClick={() => remove(index)}>删除</Button>
                              )
                            }
                          ]}
                          scroll={{ x: 720 }}
                          locale={{ emptyText: '暂无VIP奖励' }}
                        />
                      )}
                    </Form.List>
                  </div>
                </div>
              </Form>
            </Card>

            <div className="bubble-config-side">
              <Card className="section-card bubble-side-card" bodyStyle={{ padding: 22 }}>
                <div className="bubble-side-card-head">
                  <div className="bubble-side-card-title">运行状态</div>
                </div>
                <div className="bubble-runtime-grid">
                  <div className="bubble-runtime-item">
                    <span>当前状态</span>
                    <strong>{config?.enabled ? '启用中' : '未启用'}</strong>
                  </div>
                  <div className="bubble-runtime-item">
                    <span>发放间隔</span>
                    <strong>{intervalLabel}</strong>
                  </div>
                  <div className="bubble-runtime-item">
                    <span>最近发放</span>
                    <strong>{config?.last_grant_time || '--'}</strong>
                  </div>
                  <div className="bubble-runtime-item">
                    <span>下一次预计</span>
                    <strong>{config?.next_grant_time || '--'}</strong>
                  </div>
                </div>
              </Card>

              <Card className="section-card bubble-side-card" bodyStyle={{ padding: 22 }}>
                <div className="bubble-side-card-head">
                  <div className="bubble-side-card-title">奖励摘要</div>
                </div>
                <div className="bubble-reward-summary-list">
                  {renderRewardSummary('基础奖励', baseRewardItems, 'blue')}
                  {renderRewardSummary('VIP奖励', vipRewardItems, 'gold')}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}

      {activeWorkspace === 'online' && (
        <>
          <div className="mall-section-banner">
            <div className="mall-section-banner-copy">
              <div className="mall-section-banner-title">在线列表</div>
            </div>
            <div className="mall-section-banner-tags">
              <Tag color="blue">当前在线 {statInfo.onlineCount}</Tag>
              <Tag color="gold">在线 VIP {statInfo.vipOnlineCount}</Tag>
              <Tag color={config?.enabled ? 'green' : 'default'}>{config?.enabled ? '规则启用中' : '规则未启用'}</Tag>
            </div>
          </div>

          <div className="mall-command-panel">
            <div className="mall-command-panel-main bubble-command-statuses">
              <div className="bubble-inline-chip">
                <span>在线判定</span>
                <strong>{config?.online_keepalive_seconds || '--'} 秒</strong>
              </div>
              <div className="bubble-inline-chip">
                <span>普通奖励</span>
                <strong>{baseRewardItems.length} 项</strong>
              </div>
              <div className="bubble-inline-chip">
                <span>VIP奖励</span>
                <strong>{vipRewardItems.length} 项</strong>
              </div>
            </div>
            <div className="mall-command-panel-actions">
              <Button icon={<ReloadOutlined />} onClick={() => loadOnlineUsers(1, onlinePagination.pageSize)} className="rounded-button">刷新</Button>
              <Button type="primary" icon={<PlayCircleOutlined />} loading={running} onClick={handleRunNow} className="rounded-button">立即执行</Button>
            </div>
          </div>

          <Card className="section-card mall-data-card" loading={loading} bodyStyle={{ padding: 0 }}>
            <div className="mall-data-card-head">
              <div>
                <div className="mall-data-card-title">当前在线玩家</div>
              </div>
              <Tag color="blue">当前页 {onlineUsers.length} 条</Tag>
            </div>
            <Table
              rowKey="user_id"
              dataSource={onlineUsers}
              columns={onlineColumns}
              pagination={{
                ...onlinePagination,
                showSizeChanger: true,
                onChange: (page, pageSize) => loadOnlineUsers(page, pageSize)
              }}
              scroll={{ x: 860 }}
              locale={{ emptyText: '暂无在线玩家' }}
            />
          </Card>
        </>
      )}

      {activeWorkspace === 'vip' && (
        <>
          <div className="mall-section-banner">
            <div className="mall-section-banner-copy">
              <div className="mall-section-banner-title">VIP记录</div>
            </div>
            <div className="mall-section-banner-tags">
              <Tag color="blue">总记录 {vipPagination.total || 0}</Tag>
              <Tag color="green">生效中 {currentVipActiveCount}</Tag>
              <Tag color="default">已失效 {currentVipExpiredCount}</Tag>
            </div>
          </div>

          <div className="mall-command-panel">
            <div className="mall-command-panel-main">
              <Input.Search
                placeholder="搜索玩家ID、角色名、商品"
                className="field-width-240"
                allowClear
                value={vipKeyword}
                prefix={<SearchOutlined />}
                onChange={(e) => {
                  const value = e.target.value;
                  setVipKeyword(value);
                  if (!value) {
                    loadVipRecords(1, vipPagination.pageSize, { keyword: undefined });
                  }
                }}
                onSearch={(value) => {
                  setVipKeyword(value);
                  loadVipRecords(1, vipPagination.pageSize, { keyword: value });
                }}
              />
              <Select
                allowClear
                placeholder="状态"
                className="field-width-120"
                value={vipStatus}
                onChange={(value) => {
                  setVipStatus(value);
                  loadVipRecords(1, vipPagination.pageSize, { status: value });
                }}
              >
                <Option value="active">生效中</Option>
                <Option value="expired">已失效</Option>
              </Select>
            </div>
            <div className="mall-command-panel-actions">
              <Button
                type="primary"
                onClick={() => {
                  manualVipForm.setFieldsValue({
                    product_name: '后台手动发放',
                    duration_value: 30,
                    duration_unit: 'day'
                  });
                  setManualVipVisible(true);
                }}
                className="rounded-button"
              >
                手动发放VIP
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => loadVipRecords(1, vipPagination.pageSize)} className="rounded-button">刷新</Button>
            </div>
          </div>

          <Card className="section-card mall-data-card" loading={loading} bodyStyle={{ padding: 0 }}>
            <div className="mall-data-card-head">
              <div>
                <div className="mall-data-card-title">泡点VIP记录</div>
              </div>
              <Tag color="blue">当前页 {vipRecords.length} 条</Tag>
            </div>
            <Table
              rowKey={(record) => record.id || `${record.user_id}-${record.last_order_no || record.start_time || 'vip'}`}
              dataSource={vipRecords}
              columns={vipColumns}
              pagination={{
                ...vipPagination,
                showSizeChanger: true,
                onChange: (page, pageSize) => loadVipRecords(page, pageSize)
              }}
              scroll={{ x: 1350 }}
              locale={{ emptyText: '暂无VIP记录' }}
            />
          </Card>
        </>
      )}

      {activeWorkspace === 'logs' && (
        <>
          <div className="mall-section-banner">
            <div className="mall-section-banner-copy">
              <div className="mall-section-banner-title">发放日志</div>
            </div>
            <div className="mall-section-banner-tags">
              <Tag color="blue">总日志 {logPagination.total || 0}</Tag>
              <Tag color="gold">VIP日志 {currentLogVipCount}</Tag>
              <Tag color="green">普通日志 {Math.max(grantLogs.length - currentLogVipCount, 0)}</Tag>
            </div>
          </div>

          <div className="mall-command-panel">
            <div className="mall-command-panel-main bubble-command-statuses">
              <div className="bubble-inline-chip">
                <span>最近发放</span>
                <strong>{config?.last_grant_time || '--'}</strong>
              </div>
              <div className="bubble-inline-chip">
                <span>普通奖励</span>
                <strong>{baseRewardItems.length} 项</strong>
              </div>
              <div className="bubble-inline-chip">
                <span>VIP奖励</span>
                <strong>{vipRewardItems.length} 项</strong>
              </div>
            </div>
            <div className="mall-command-panel-actions">
              <Button icon={<ReloadOutlined />} onClick={() => loadLogs(1, logPagination.pageSize)} className="rounded-button">刷新</Button>
              <Button type="primary" icon={<PlayCircleOutlined />} loading={running} onClick={handleRunNow} className="rounded-button">立即执行</Button>
            </div>
          </div>

          <Card className="section-card mall-data-card" loading={loading} bodyStyle={{ padding: 0 }}>
            <div className="mall-data-card-head">
              <div>
                <div className="mall-data-card-title">泡点发放日志</div>
              </div>
              <Tag color="blue">当前页 {grantLogs.length} 条</Tag>
            </div>
            <Table
              rowKey="id"
              dataSource={grantLogs}
              columns={logColumns}
              pagination={{
                ...logPagination,
                showSizeChanger: true,
                onChange: (page, pageSize) => loadLogs(page, pageSize)
              }}
              scroll={{ x: 1120 }}
              locale={{ emptyText: '暂无发放日志' }}
            />
          </Card>
        </>
      )}

      <Modal
        title={null}
        open={rewardPickerVisible}
        onCancel={() => setRewardPickerVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setRewardPickerVisible(false)}>取消</Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmRewardPicker}>
            确认选择 ({pickerSelectedItems.length}个道具)
          </Button>
        ]}
        width={1120}
        className="mail-item-modal"
      >
        <div className="mail-item-picker">
          <div className="mail-modal-hero mail-modal-hero-compact">
            <div>
              <div className="mail-modal-title">{rewardPickerType === 'base_items' ? '基础奖励选择' : 'VIP奖励选择'}</div>
            </div>
            <div className="mail-modal-hero-tags">
              <Tag color="blue">可选 {filteredItemCatalog.length} 个</Tag>
              <Tag color="green">已选 {pickerSelectedItems.length} 个</Tag>
            </div>
          </div>

          <div className="mail-item-picker-toolbar">
            <Input
              placeholder="搜索道具：输入 ID 或名称"
              allowClear
              value={pickerSearchText}
              onChange={(e) => setPickerSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              className="mail-item-search"
            />
          </div>

          <div className="mail-item-picker-body">
            <div className="mail-item-library">
              <div className="mail-item-grid">
                {filteredItemCatalog.map((record) => {
                  const isAdded = record.levels && record.levels.length > 0
                    ? record.levels.some((lvl) => pickerSelectedItems.some((selectedItem) => selectedItem.uniqueId === `${record.id}_${lvl.level}`))
                    : pickerSelectedItems.some((selectedItem) => selectedItem.uniqueId === `${record.id}_${record.defaultLevel || 0}`);
                  const colors = ['default', 'green', 'blue', 'purple', 'orange', 'red'];

                  return (
                    <div key={record.id} className={`mail-item-card${isAdded ? ' mail-item-card-added' : ''}`}>
                      <div className="mail-item-card-head">
                        <div>
                          <div className="mail-item-card-title">{record.name || '未知道具'}</div>
                          <div className="mail-item-card-subtitle">ID {record.id}</div>
                        </div>
                        <Tag color={colors[record.quality] || 'default'}>{record.quality || 0}星</Tag>
                      </div>
                      <div className="mail-item-card-meta">
                        <span>{record.levels && record.levels.length > 0 ? `${record.levels.length} 个等级` : '单等级道具'}</span>
                        <span>{record.type || 'item'}</span>
                      </div>
                      <div className="mail-item-card-desc">{record.description || '暂无描述'}</div>
                      <Button
                        type={isAdded ? 'default' : 'primary'}
                        disabled={isAdded}
                        onClick={() => handleSelectRewardItem(record)}
                        block
                      >
                        {isAdded ? '已添加' : '加入奖励'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mail-item-sidebar">
              <div className="mail-form-section mail-form-section-tight">
                <div className="mail-form-section-head">
                  <div className="mail-form-section-title">当前奖励清单</div>
                </div>
                {pickerSelectedItems.length > 0 ? (
                  <div className="mail-selected-list">
                    {pickerSelectedItems.map((item) => (
                      <div key={item.uniqueId || item.id} className="mail-selected-item">
                        <div className="mail-selected-item-main">
                          <div className="mail-selected-item-title">{item.name || `道具${item.id}`}{item.level ? ` Lv.${item.level}` : ''}</div>
                          <div className="mail-selected-item-meta">道具 ID {item.id}</div>
                        </div>
                        <div className="mail-selected-item-controls">
                          <InputNumber
                            min={1}
                            max={MAX_REWARD_COUNT}
                            value={item.count}
                            onChange={(value) => setPickerSelectedItems(pickerSelectedItems.map((current) => (
                              (current.uniqueId || current.id) === (item.uniqueId || item.id)
                                ? { ...current, count: parseInt(value, 10) || 1 }
                                : current
                            )))}
                            addonBefore="数量"
                            size="small"
                            className="field-width-120"
                          />
                          <Button
                            type="text"
                            danger
                            size="small"
                            onClick={() => setPickerSelectedItems(pickerSelectedItems.filter((current) => (current.uniqueId || current.id) !== (item.uniqueId || item.id)))}
                          >
                            移除
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mail-empty-panel">
                    <div className="mail-empty-panel-title">右侧清单为空</div>
                    <div className="mail-empty-panel-text">从左侧加入道具后显示。</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="手动发放泡点VIP"
        open={manualVipVisible}
        onCancel={() => {
          setManualVipVisible(false);
          manualVipForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={manualVipForm}
          layout="vertical"
          onFinish={handleManualVipSubmit}
          initialValues={{
            product_name: '后台手动发放',
            duration_value: 30,
            duration_unit: 'day'
          }}
        >
          <Form.Item
            name="user_id"
            label="玩家ID"
            rules={[{ required: true, message: '请输入玩家ID' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入玩家ID" />
          </Form.Item>
          <Form.Item
            name="product_name"
            label="商品名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="例如：后台补发30天泡点VIP" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="duration_value"
                label="时长数值"
                rules={[{ required: true, message: '请输入时长' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="duration_unit"
                label="时长单位"
                rules={[{ required: true, message: '请选择单位' }]}
              >
                <Select>
                  <Option value="minute">分钟</Option>
                  <Option value="hour">小时</Option>
                  <Option value="day">天</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button onClick={() => {
              setManualVipVisible(false);
              manualVipForm.resetFields();
            }}>
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={manualVipSubmitting}>
              确认发放
            </Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={`选择等级 - ${currentItemForLevel?.name || ''}`}
        open={levelModalVisible}
        onCancel={() => {
          setLevelModalVisible(false);
          setCurrentItemForLevel(null);
          setSelectedLevel(null);
        }}
        onOk={handleConfirmPickerLevel}
        width={440}
        okText="确认选择"
        cancelText="取消"
      >
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          <Table
            columns={[
              { title: '等级', dataIndex: 'level', key: 'level', width: 70, render: (level) => <Tag color="blue">Lv.{level}</Tag> },
              { title: '品质', dataIndex: 'quality', key: 'quality', width: 70, render: (quality) => <Tag>{quality || '-'}</Tag> },
              { title: '描述', dataIndex: 'description', key: 'description', width: 150, ellipsis: true, render: (text) => <Text type="secondary" ellipsis={{ tooltip: text || '-' }}>{text || '-'}</Text> },
              {
                title: '操作', key: 'action', width: 88,
                render: (_, record) => (
                  <Button size="small" type={selectedLevel === record.level ? 'primary' : 'default'} onClick={() => setSelectedLevel(record.level)}>
                    {selectedLevel === record.level ? '已选' : '选择'}
                  </Button>
                )
              }
            ]}
            dataSource={currentItemForLevel?.levels || []}
            rowKey="level"
            pagination={false}
            size="small"
            tableLayout="fixed"
            scroll={{ x: 378 }}
          />
        </div>
      </Modal>

    </div>
  );
}

export default BubbleRewardManagement;
